// ContractList.js
import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  Paper,
  Stack,
  Snackbar,
  Alert,
  useTheme,
  Button,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  Refresh,
  Visibility,
  DragIndicator,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Link } from "react-router-dom";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import ConfirmDialog from "../ui/ConfirmDialog";
import {
  CONTRACT_STATUS,
  CONTRACT_STATUS_LABELS,
} from "../../utils/models/contractModel";
import { PROJECT_STATUS } from "../../utils/models/projectModel";

const STATUS_ORDER = [
  CONTRACT_STATUS.PENDING,
  CONTRACT_STATUS.CREATED,
  CONTRACT_STATUS.SENT,
  CONTRACT_STATUS.MODIFICATION,
  CONTRACT_STATUS.SIGNED,
];

const ITEMS_PER_PAGE = 3;

const ContractList = ({ onView, onEdit, filter = {} }) => {
  const theme = useTheme();
  const { data: contracts, loading } = useCollection("contracts");
  const { remove, update: updateContract } = useAuditFirestore("contracts");
  const { add: addProject } = useAuditFirestore("projects");

  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [localContracts, setLocalContracts] = useState(null);
  const [visibleCounts, setVisibleCounts] = useState({});

  React.useEffect(() => {
    if (contracts) {
      setLocalContracts(contracts);
    }
  }, [contracts]);

  const filtered = localContracts?.filter((c) => {
    if (c.isActive !== true) return false;

    if (search) {
      const searchLower = search.toLowerCase();
      return (
        c.title?.toLowerCase().includes(searchLower) ||
        c.clientName?.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  const grouped = useMemo(() => {
    const groups = {};

    STATUS_ORDER.forEach((status) => {
      groups[status] =
        filtered
          ?.filter((c) => c.status === status)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) || [];
    });

    return groups;
  }, [filtered]);

  const getVisibleItems = (status, items) => {
    const max = visibleCounts[status] || ITEMS_PER_PAGE;
    return items.slice(0, max);
  };

  const hasMore = (status, items) => {
    const max = visibleCounts[status] || ITEMS_PER_PAGE;
    return items.length > max;
  };

  const canShowLess = (status) => {
    return (visibleCounts[status] || ITEMS_PER_PAGE) > ITEMS_PER_PAGE;
  };

  const showMore = (status) => {
    setVisibleCounts((prev) => ({
      ...prev,
      [status]: (prev[status] || ITEMS_PER_PAGE) + ITEMS_PER_PAGE,
    }));
  };

  const showLess = (status) => {
    setVisibleCounts((prev) => ({
      ...prev,
      [status]: Math.max(
        ITEMS_PER_PAGE,
        (prev[status] || ITEMS_PER_PAGE) - ITEMS_PER_PAGE,
      ),
    }));
  };

  const handleDragEnd = useCallback(
    async (result) => {
      const { source, destination, draggableId } = result;

      if (!destination) return;

      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      const newStatus = destination.droppableId;
      const oldStatus = source.droppableId;

      const contract = localContracts?.find((c) => c.id === draggableId);
      if (!contract) return;

      let updatedData = {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      };

      if (
        newStatus === CONTRACT_STATUS.MODIFICATION &&
        oldStatus !== CONTRACT_STATUS.MODIFICATION
      ) {
        const currentVersion = parseFloat(contract.version || "1.0");
        const newVersion = (currentVersion + 0.1).toFixed(1);
        const newVersionEntry = {
          version: newVersion,
          date: new Date().toISOString(),
          previousStatus: oldStatus,
          notes: `Modificación desde estado: ${CONTRACT_STATUS_LABELS[oldStatus]}`,
        };

        updatedData = {
          ...updatedData,
          version: newVersion,
          versions: [...(contract.versions || []), newVersionEntry],
        };
      }

      setLocalContracts((prev) =>
        prev.map((c) => (c.id === draggableId ? { ...c, ...updatedData } : c)),
      );

      try {
        await updateContract(draggableId, updatedData);

        if (
          newStatus === CONTRACT_STATUS.SIGNED &&
          oldStatus !== CONTRACT_STATUS.SIGNED
        ) {
          await addProject({
            contractId: contract.id,
            contractTitle: contract.title,
            contractSignedDate: new Date().toISOString(),
            name:
              contract.title ||
              `Proyecto de ${contract.clientName || "cliente"}`,
            description: contract.description || "", // 👈 Fix: default to empty string
            clientId: contract.clientId,
            clientName: contract.clientName,
            clientPhone: contract.clientPhone || "",
            address: contract.address || {}, // 👈 Also ensure address isn't undefined
            status: PROJECT_STATUS.MANAGING,
            budget: 0,
            team: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: "",
            isActive: true,
            notes: `Proyecto generado automáticamente al firmar contrato: ${contract.title}`,
          });

          setSuccessMessage(
            "✅ Contrato firmado y proyecto creado automáticamente",
          );
        }
      } catch (error) {
        console.error("Error:", error);
        setLocalContracts((prev) =>
          prev.map((c) =>
            c.id === draggableId ? { ...c, status: oldStatus } : c,
          ),
        );
        setErrorMessage("Error al actualizar: " + error.message);
      }
    },
    [updateContract, addProject, localContracts],
  );

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setLocalContracts((prev) => prev.filter((c) => c.id !== deleteTarget.id));
    const deletedContract = deleteTarget;
    setDeleteTarget(null);

    try {
      await remove(deletedContract.id);
    } catch (error) {
      console.error("Error deleting:", error);
      setLocalContracts((prev) => [...prev, deletedContract]);
      setErrorMessage("Error al eliminar el contrato");
    }
  };

  if (loading || !localContracts) return <LoadingSpinner />;

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "20px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "#e2e8f0",
          background: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            placeholder="Buscar por título o cliente"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              flex: 1,
              minWidth: { xs: "100%", sm: "260px" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                background: theme.palette.background.paper,
                height: 44,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    ✖
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box
            sx={{ display: "flex", alignItems: "center", height: 44, gap: 1 }}
          >
            <Chip
              label={`Total: ${filtered?.length || 0}`}
              sx={{
                height: 44,
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                background: theme.palette.background.paper,
                border: "1px solid",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              }}
            />
            <Tooltip title="Refrescar">
              <IconButton
                onClick={() => {
                  if (contracts) setLocalContracts(contracts);
                }}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 175, 163, 0.1)"
                        : "rgba(0, 95, 101, 0.05)",
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {Object.values(grouped).every((arr) => arr.length === 0) ? (
        <EmptyState
          title={search ? "Sin resultados" : "Sin contratos"}
          description={
            search ? "Prueba con otros términos" : "Añade tu primer contrato"
          }
          actionLabel="Nuevo Contrato"
          onAction={() => onEdit(null)}
        />
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(5, 1fr)",
              },
              gap: { xs: 1.5, sm: 2, md: 2.5 },
              width: "100%",
            }}
          >
            {Object.entries(grouped).map(([status, items]) => {
              const visibleItems = getVisibleItems(status, items);
              const showMoreButton = hasMore(status, items);
              const showLessButton = canShowLess(status);
              const remaining = items.length - visibleItems.length;
              const showing = visibleItems.length;
              const total = items.length;

              return (
                <Box key={status} sx={{ minWidth: 0 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: "16px",
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.1)"
                          : "rgba(0, 147, 93, 0.2)",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(0, 80, 45, 0.08)"
                          : "rgba(0, 255, 187, 0.05)",
                      minHeight: 200,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color="text.secondary"
                        noWrap
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                      >
                        {CONTRACT_STATUS_LABELS[status]}
                      </Typography>
                      <Chip
                        label={items.length}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        }}
                      />
                    </Box>

                    <Droppable droppableId={status}>
                      {(provided, snapshot) => (
                        <Stack
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          spacing={1.5}
                          sx={{
                            flex: 1,
                            minHeight: 80,
                            background: snapshot.isDraggingOver
                              ? theme.palette.mode === "dark"
                                ? "rgba(0, 192, 124, 0.15)"
                                : "rgba(0, 192, 124, 0.1)"
                              : "transparent",
                            borderRadius: "12px",
                            border: snapshot.isDraggingOver
                              ? `2px dashed ${theme.palette.secondary.light || "#00c07c"}`
                              : "2px dashed transparent",
                            transition: "all 0.2s",
                            p: 1,
                          }}
                        >
                          {visibleItems.map((contract, index) => (
                            <Draggable
                              key={contract.id}
                              draggableId={contract.id}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <Card
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  elevation={0}
                                  sx={{
                                    borderRadius: "12px",
                                    border: "1px solid",
                                    borderColor: snapshot.isDragging
                                      ? theme.palette.secondary.main
                                      : theme.palette.mode === "dark"
                                        ? "rgba(0, 192, 124, 0.25)"
                                        : "rgba(0, 147, 93, 0.3)",
                                    background: snapshot.isDragging
                                      ? theme.palette.mode === "dark"
                                        ? "rgba(0, 192, 124, 0.25)"
                                        : "rgba(0, 255, 187, 0.2)"
                                      : theme.palette.mode === "dark"
                                        ? "rgba(0, 147, 93, 0.2)"
                                        : "rgba(0, 255, 187, 0.15)",
                                    cursor: snapshot.isDragging
                                      ? "grabbing"
                                      : "default",
                                    transition:
                                      "box-shadow 0.2s, transform 0.2s, background 0.2s",
                                    boxShadow: snapshot.isDragging
                                      ? `0 8px 24px rgba(0, 147, 93, 0.3)`
                                      : "none",
                                    "&:hover": {
                                      boxShadow: snapshot.isDragging
                                        ? `0 8px 24px rgba(0, 147, 93, 0.3)`
                                        : `0 4px 16px rgba(0, 147, 93, 0.15)`,
                                      transform: snapshot.isDragging
                                        ? "none"
                                        : "translateY(-2px)",
                                    },
                                  }}
                                >
                                  <CardContent
                                    sx={{
                                      p: { xs: 1.5, sm: 2 },
                                      "&:last-child": {
                                        pb: { xs: 1.5, sm: 2 },
                                      },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        mb: 1,
                                      }}
                                    >
                                      <Box
                                        {...provided.dragHandleProps}
                                        sx={{
                                          mr: 1,
                                          mt: 0.5,
                                          color: "text.disabled",
                                          cursor: "grab",
                                          display: "flex",
                                          alignItems: "center",
                                          "&:hover": {
                                            color: theme.palette.primary.main,
                                          },
                                          "&:active": { cursor: "grabbing" },
                                        }}
                                      >
                                        <DragIndicator
                                          sx={{ fontSize: { xs: 18, sm: 20 } }}
                                        />
                                      </Box>

                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                          variant="body2"
                                          fontWeight={600}
                                          sx={{
                                            cursor: "pointer",
                                            mb: 0.5,
                                            wordBreak: "break-word",
                                            fontSize: {
                                              xs: "0.75rem",
                                              sm: "0.85rem",
                                            },
                                          }}
                                          onClick={() => onView(contract)}
                                        >
                                          {contract.title || "Sin título"}
                                        </Typography>

                                        {contract.version && (
                                          <Typography
                                            variant="caption"
                                            color="primary"
                                            sx={{
                                              display: "block",
                                              fontWeight: 600,
                                              mb: 0.5,
                                              fontSize: {
                                                xs: "0.6rem",
                                                sm: "0.7rem",
                                              },
                                            }}
                                          >
                                            v{contract.version}
                                          </Typography>
                                        )}

                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                          sx={{
                                            mb: 0.5,
                                            wordBreak: "break-word",
                                            fontSize: {
                                              xs: "0.6rem",
                                              sm: "0.7rem",
                                            },
                                          }}
                                        >
                                          {contract.address?.street
                                            ? `${contract.address.street}, ${contract.address.city || ""} ${contract.address.province || ""}`
                                            : "Sin dirección"}
                                        </Typography>

                                        <Box sx={{ mt: 0.5, display: "block" }}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                              display: "block",
                                              mb: 0,
                                              fontWeight: 500,
                                              wordBreak: "break-word",
                                              fontSize: {
                                                xs: "0.6rem",
                                                sm: "0.7rem",
                                              },
                                            }}
                                          >
                                            {contract.clientName ||
                                              "Sin cliente"}
                                          </Typography>
                                        </Box>

                                        {contract.proposalId && (
                                          <Link
                                            to={`/proposals/${contract.proposalId}`}
                                            style={{ textDecoration: "none" }}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="primary"
                                              sx={{
                                                display: "block",
                                                mb: 1,
                                                fontSize: {
                                                  xs: "0.6rem",
                                                  sm: "0.7rem",
                                                },
                                              }}
                                              noWrap
                                            >
                                              📄 Ver propuesta
                                            </Typography>
                                          </Link>
                                        )}
                                      </Box>
                                    </Box>

                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        ml: 4,
                                        gap: 1,
                                      }}
                                    >
                                      <Stack direction="row" spacing={0.5}>
                                        <Tooltip title="Ver detalle">
                                          <IconButton
                                            size="small"
                                            onClick={() => onView(contract)}
                                            sx={{
                                              color: theme.palette.info.main,
                                            }}
                                          >
                                            <Visibility
                                              sx={{
                                                fontSize: { xs: 18, sm: 20 },
                                              }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Editar">
                                          <IconButton
                                            size="small"
                                            onClick={() => onEdit(contract)}
                                            sx={{
                                              color: theme.palette.primary.main,
                                            }}
                                          >
                                            <Edit
                                              sx={{
                                                fontSize: { xs: 18, sm: 20 },
                                              }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Eliminar">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              setDeleteTarget(contract)
                                            }
                                            sx={{
                                              color: theme.palette.error.main,
                                            }}
                                          >
                                            <Delete
                                              sx={{
                                                fontSize: { xs: 18, sm: 20 },
                                              }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                    </Box>
                                  </CardContent>
                                </Card>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Stack>
                      )}
                    </Droppable>

                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      {showMoreButton && (
                        <Button
                          fullWidth
                          size="small"
                          onClick={() => showMore(status)}
                          endIcon={<ExpandMore />}
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: { xs: "0.65rem", sm: "0.75rem" },
                            color: theme.palette.primary.main,
                            borderColor: theme.palette.primary.main,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(0, 175, 163, 0.1)"
                                  : "rgba(0, 95, 101, 0.05)",
                            },
                          }}
                          variant="outlined"
                        >
                          Ver {Math.min(remaining, ITEMS_PER_PAGE)} más (
                          {showing}/{total})
                        </Button>
                      )}
                      {showLessButton && (
                        <Button
                          fullWidth
                          size="small"
                          onClick={() => showLess(status)}
                          endIcon={<ExpandLess />}
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: { xs: "0.65rem", sm: "0.75rem" },
                            color: theme.palette.text.secondary,
                            borderColor: theme.palette.divider,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.05)"
                                  : "rgba(0, 0, 0, 0.03)",
                            },
                          }}
                          variant="outlined"
                        >
                          Ver menos
                        </Button>
                      )}
                    </Box>
                  </Paper>
                </Box>
              );
            })}
          </Box>
        </DragDropContext>
      )}

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar Contrato"
        message={`¿Eliminar el contrato "${deleteTarget?.title}"?`}
        type="delete"
      />
    </Box>
  );
};

export default ContractList;
